import React, { useState } from 'react';
import { Form, Field } from 'react-final-form';
import { Button, Input, InputLabel, InputError, Topography, Select } from 'components/ui';
import Alert from 'components/ui/Alert/Alert';
import { useDispatch } from 'react-redux';
import { addNewPayment } from 'store/actions/paymentMethods';
import { Validators, Parsers } from 'utils';
import MapSearchSuggestions from 'components/ui/MapSearchSuggestions/MapSearchSuggestions.js'

type CardSetupFormProps = {
  handleCloseForm?: () => void;
};

const states = ['AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'IA', 'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MO', 'ME', 'MI', 'MN', 'MD', 'MS', 'MT', 'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY']

const CardSetupForm: React.FC<CardSetupFormProps> = ({ handleCloseForm }) => {
  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState<string>();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    setIsSaving(true);
    setErrorMessage('')
    let data = {
      card_number: values.cardNumber.replace(/\D/g, ''),
      card_code: values.cardCode,
      card_month: values.cardExp?.split('/')[0] && parseInt(values.cardExp?.split('/')[0]) || '',
      card_year: values.cardExp?.split('/')[1] && parseInt(values.cardExp?.split('/')[1] || ''),
      name: values.name,
      address1: values.address1,
      address2: values.address2,
      state: values.state?.value,
      city: values.city,
      zip: values.zipCode,
    }
    dispatch(addNewPayment(data, (err: any, res: any) => {
      setIsSaving(false)
      if (err) setErrorMessage(err)
      else handleCloseForm && handleCloseForm()
    }))
  };

  const handleSuggestionSelect = (suggestion: any, form: any) => {
    let city = '', state = '', address1 = '', zipCode = suggestion.zipCode;
    address1 = suggestion.formattedSuggestion?.mainText
    let extras = suggestion.formattedSuggestion?.secondaryText?.split(',') || []
    if (extras.length == 3) {
      city = extras[0]?.trim()
      state = extras[1]?.trim()
    } else if (extras.length == 2) state = extras[0]?.trim()
    if (!states.includes(state)) state = ''
    form.change('address1', address1)
    form.change('city', city)
    form.change('state', { label: state, value: state })
    form.change('zipCode', zipCode)
  }

  return (
    <div className='row'>
      <div className='col-12 mt-3'>
        <Form onSubmit={handleSubmit} render={({ handleSubmit, hasValidationErrors, form }) => (
          <form onSubmit={handleSubmit} className='row'>
            <div className='col-12'>
              <Topography.H4>Billing Information</Topography.H4>
            </div>
            <Field name="name" validate={Validators.compose(Validators.required)} parse={Parsers.notNumber}>
              {({ input, meta }) => {
                return (
                  <div className='col-12 col-md-4 mb-3'>
                    <InputLabel text='NAME *' />
                    <Input {...input} type="text" hasError={meta.error && meta.touched} maxLength={25} />
                    {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                  </div>
                )
              }}
            </Field>
            <Field name="address1" validate={Validators.compose(Validators.required)}>
              {({ input, meta }) => {
                return (
                  <div className='col-12 col-md-4 mb-3'>
                    <InputLabel text='ADDRESS 1 *' />
                    <MapSearchSuggestions
                      value={input.value}
                      onChange={input.onChange}
                      onSelect={(s: any) => handleSuggestionSelect(s, form)}
                      render={(props: any) => <Input {...input} {...props} type="text" hasError={meta.error && meta.touched} />}
                    />
                    {/* <Input {...input} type="text" hasError={meta.error && meta.touched} maxLength={50} /> */}
                    {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                  </div>
                )
              }}
            </Field>
            <Field name="address2">
              {({ input, meta }) => {
                return (
                  <div className='col-12 col-md-4 mb-3'>
                    <InputLabel text='ADDRESS 2' />
                    <Input {...input} type="text" hasError={meta.error && meta.touched} maxLength={50} />
                    {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                  </div>
                )
              }}
            </Field>
            <Field name="state" validate={Validators.compose(Validators.required)}>
              {({ input, meta }) => {
                return (
                  <div className='col-12 col-md-4 mb-3'>
                    <InputLabel text='STATE *' />
                    <Select {...input} options={states.map(state => ({ label: state, value: state }))} hasError={meta.error && meta.touched} maxLength={25} />
                    {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                  </div>
                )
              }}
            </Field>
            <Field name="city" validate={Validators.compose(Validators.required)}>
              {({ input, meta }) => {
                return (
                  <div className='col-6 col-md-4 mb-3'>
                    <InputLabel text='CITY *' />
                    <Input {...input} type="text" hasError={meta.error && meta.touched} maxLength={20} />
                    {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                  </div>
                )
              }}
            </Field>
            <Field name="zipCode" validate={Validators.compose(Validators.required)} parse={Parsers.number}>
              {({ input, meta }) => {
                return (
                  <div className='col-6 col-md-4 mb-3'>
                    <InputLabel text='ZIP CODE *' />
                    <Input {...input} type="text" hasError={meta.error && meta.touched} maxLength={6} />
                    {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                  </div>
                )
              }}
            </Field>
            <div className='col-12 mt-3'>
              <Topography.H4>Card Details</Topography.H4>
            </div>
            <Field name="cardNumber" validate={Validators.compose(Validators.required, Validators.cardNumber)} parse={Parsers.cardNumber}>
              {({ input, meta }) => {
                return (
                  <div className='col-12 col-md-4 mb-3'>
                    <InputLabel text='CREDIT OR DEBIT CARD *' />
                    <Input {...input} type="text" hasError={meta.error && meta.touched} placeholder='1234 1234 1234 1234' maxLength={19} />
                    {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                  </div>
                )
              }}
            </Field>
            <Field name="cardExp" validate={Validators.compose(Validators.required, Validators.cardExpDate)} parse={Parsers.cardExpDate}>
              {({ input, meta }) => (
                <div className='col-12 col-md-4 mb-3'>
                  <InputLabel text='EXPIRATION DATE *' />
                  <Input {...input} type="text" hasError={meta.error && meta.touched} placeholder='MM / YY' />
                  {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                </div>
              )}
            </Field>
            <Field name="cardCode" validate={Validators.compose(Validators.required, Validators.cardCVC)} parse={Parsers.cvc}>
              {({ input, meta }) => (
                <div className='col-12 col-md-4 mb-3 mb-5'>
                  <InputLabel text='CVV *' />
                  <Input {...input} type="text" hasError={meta.error && meta.touched} maxLength={4} />
                  {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                </div>
              )}
            </Field>
            <div className='col-12 mb-3'>
              {errorMessage && <Alert type="error">{errorMessage}</Alert>}
            </div>
            <div className='col-auto'>
              <Button disabled={hasValidationErrors || isSaving} htmlType="submit" className='w-auto px-4'>{isSaving ? 'Saving' : 'Save Card'}</Button>
            </div>
            <div className='col-auto'>
              {handleCloseForm && <Button onClick={handleCloseForm} className='w-auto px-4'>Cancel</Button>}
            </div>
          </form>
        )} />
      </div>
    </div>
  );
};

export default CardSetupForm;
